import React, { useState, useEffect } from 'react';
import { XMarkIcon, MegaphoneIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const AnnouncementPopup = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  useEffect(() => {
    checkForUnseenAnnouncements();
  }, []);

  const checkForUnseenAnnouncements = async () => {
    try {
      const response = await api.get('/portal/announcements/unseen');
      // Ensure response.data is an array and has items
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        setAnnouncements(response.data);
        setShow(true);
      } else {
        setAnnouncements([]);
        setShow(false);
      }
    } catch (error) {
      console.error('Error loading unseen announcements:', error);
      setAnnouncements([]);
      setShow(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    try {
      // Mark all announcements as viewed
      const announcementIds = announcements.map(a => a.id);
      await api.post('/portal/announcements/view-multiple', { announcementIds });
      setShow(false);
    } catch (error) {
      console.error('Error marking announcements as viewed:', error);
      setShow(false);
    }
  };

  const handleNext = async () => {
    // Mark current announcement as viewed
    try {
      await api.post(`/portal/announcements/${announcements[currentIndex].id}/view`);
    } catch (error) {
      console.error('Error marking announcement as viewed:', error);
    }

    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setShow(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  if (loading || !show || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];

  // Safety check: if currentAnnouncement is undefined, don't render
  if (!currentAnnouncement) {
    return null;
  }
  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      normal: 'bg-blue-100 text-blue-800 border-blue-300',
      low: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[priority] || colors.normal;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden animate-fade-in">
        {/* Header */}
        <div style={{ backgroundColor: 'var(--color-primary)' }} className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <MegaphoneIcon className="h-6 w-6 text-white" />
            <h3 className="text-lg font-semibold text-white">New Announcement</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-md text-white hover:bg-white/20 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Priority Badge */}
          <div className="mb-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(currentAnnouncement.priority)}`}>
              {currentAnnouncement.priority?.toUpperCase()}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {currentAnnouncement.title}
          </h2>

          {/* Content */}
          <div className="text-gray-700 mb-6 leading-relaxed whitespace-pre-line">
            {currentAnnouncement.content}
          </div>

          {/* Meta Info */}
          <div className="flex items-center text-sm text-gray-500 space-x-4 border-t pt-4">
            <div>
              By: <span className="font-medium">{currentAnnouncement.creator_name || currentAnnouncement.creator_email}</span>
            </div>
            <div>
              Date: <span className="font-medium">{new Date(currentAnnouncement.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Footer with Navigation */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
          <div className="text-sm text-gray-600">
            Announcement {currentIndex + 1} of {announcements.length}
          </div>
          <div className="flex items-center space-x-2">
            {currentIndex > 0 && (
              <button
                onClick={handlePrevious}
                className="btn-secondary flex items-center space-x-1"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                <span>Previous</span>
              </button>
            )}
            <button
              onClick={handleNext}
              className="btn-primary flex items-center space-x-1"
            >
              <span>{currentIndex < announcements.length - 1 ? 'Next' : 'Close'}</span>
              {currentIndex < announcements.length - 1 && <ChevronRightIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;
